import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsAPI } from '@/services';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { usePermissions } from '../hooks/usePermissions';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import ModalShell from '../components/ModalShell';

const EventRegistrations = () => {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const { error, success } = useToast();
  const { confirm } = useConfirmDialog();
  const { hasPermission } = usePermissions();
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
  const [registrationsPage, setRegistrationsPage] = useState(1);
  const [registrationsTotal, setRegistrationsTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [updatingRegistration, setUpdatingRegistration] = useState(null);
  const [sendingConfirmation, setSendingConfirmation] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadEvent();
    loadRegistrations();
  }, [eventId]);

  useEffect(() => {
    filterRegistrations();
  }, [registrations, searchQuery, selectedStatus, selectedPaymentStatus]);

  const loadEvent = async () => {
    try {
      const response = await eventsAPI.getById(eventId);
      setEvent(response.data);
    } catch (err) {
      console.error('Failed to load event:', err);
      error('Erreur lors du chargement de l\'événement');
      navigate('/calendar');
    }
  };

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const response = await eventsAPI.getRegistrations(eventId, {
        limit: 1000, // Charger toutes les inscriptions pour le filtrage côté client
        skip: 0
      });
      const rows = Array.isArray(response.data)
        ? response.data
        : (response.data?.registrations || []);
      setRegistrations(rows);
      setRegistrationsTotal(
        Array.isArray(response.data)
          ? response.data.length
          : (response.data?.total || rows.length)
      );
    } catch (err) {
      console.error('Failed to load registrations:', err);
      error('Erreur lors du chargement des inscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setRegistrationsPage(1);
  }, [searchQuery, selectedStatus, selectedPaymentStatus]);

  const filterRegistrations = () => {
    let filtered = [...registrations];

    // Filtrer par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(reg => {
        const name = (reg.member_name || reg.name || '').toLowerCase();
        const firstName = (reg.first_name || '').toLowerCase();
        const email = (reg.member_email || reg.email || '').toLowerCase();
        const phone = (reg.member_phone || reg.phone || reg.whatsapp || '').toLowerCase();
        const code = (reg.confirmation_code || '').toLowerCase();
        return name.includes(query) || firstName.includes(query) || email.includes(query) || phone.includes(query) || code.includes(query);
      });
    }

    // Filtrer par statut d'inscription
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(reg => reg.registration_status === selectedStatus);
    }

    // Filtrer par statut de paiement
    if (selectedPaymentStatus !== 'all') {
      filtered = filtered.filter(reg => reg.payment_status === selectedPaymentStatus);
    }

    setFilteredRegistrations(filtered);
  };

  const filteredTotal = filteredRegistrations.length;
  const registrationsTotalPages = Math.max(1, Math.ceil(filteredTotal / itemsPerPage));
  const paginatedRegistrations = useMemo(() => {
    const start = (registrationsPage - 1) * itemsPerPage;
    return filteredRegistrations.slice(start, start + itemsPerPage);
  }, [filteredRegistrations, registrationsPage, itemsPerPage]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filteredTotal / Math.max(1, itemsPerPage)));
    setRegistrationsPage((p) => (p > tp ? tp : p));
  }, [filteredTotal, itemsPerPage]);

  useEffect(() => {
    setRegistrationsPage(1);
  }, [itemsPerPage]);

  const handleUpdateStatus = async (registrationId, newStatus) => {
    try {
      setUpdatingRegistration(registrationId);
      await eventsAPI.updateRegistration(eventId, registrationId, {
        registration_status: newStatus
      });
      success(`Statut mis à jour avec succès`);
      loadRegistrations();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingRegistration(null);
    }
  };

  const handleUpdatePaymentStatus = async (registrationId, newPaymentStatus) => {
    try {
      setUpdatingRegistration(registrationId);
      const updateData = {
        payment_status: newPaymentStatus
      };

      // Si le paiement est vérifié et que l'événement est payant, approuver automatiquement
      if (newPaymentStatus === 'verified' && !event?.is_free) {
        updateData.registration_status = 'approved';
      }

      await eventsAPI.updateRegistration(eventId, registrationId, updateData);
      success(`Statut de paiement mis à jour avec succès`);
      loadRegistrations();
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la mise à jour du statut de paiement');
    } finally {
      setUpdatingRegistration(null);
    }
  };

  const handleSendConfirmation = async (registrationId, registration) => {
    try {
      // Récupérer les détails de l'inscription pour construire le message
      const confirmationResponse = await eventsAPI.sendConfirmation(eventId, registrationId);
      const { contact, confirmation_code, whatsapp_message } = confirmationResponse.data;
      
      if (!contact) {
        error('Aucun contact disponible pour ce participant');
        return;
      }
      
      // Formater le numéro de téléphone pour WhatsApp (supprimer les caractères non numériques sauf +)
      const phone = contact.replace(/[^\d+]/g, '').replace(/^0/, ''); // Supprimer les espaces, tirets, etc. et remplacer 0 initial
      
      // Encoder le message pour l'URL
      const encodedMessage = encodeURIComponent(whatsapp_message);
      
      // Ouvrir WhatsApp avec le message
      const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      
      success('WhatsApp ouvert avec le message de confirmation');
    } catch (err) {
      console.error('Error getting confirmation details:', err);
      error(err.response?.data?.detail || 'Erreur lors de la préparation de la confirmation');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved':
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusBadgeColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading && !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50/30">
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/events/${eventId}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition mb-4 group"
          >
            <svg className="h-5 w-5 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm md:text-base font-medium">Retour à l'événement</span>
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {event ? `Inscriptions - ${event.title}` : 'Gestion des inscriptions'}
          </h1>
          <p className="text-gray-600 text-lg">{event ? `Gérez les inscriptions pour cet événement` : ''}</p>
        </div>

        {/* Statistiques */}
        {!loading && registrations.length > 0 && (
          <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
              <div className="text-sm font-medium opacity-90">Total inscriptions</div>
              <div className="mt-2 text-3xl font-bold">{registrationsTotal}</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
              <div className="text-sm font-medium opacity-90">Approuvées</div>
              <div className="mt-2 text-3xl font-bold">
                {registrations.filter(r => r.registration_status === 'approved' || r.registration_status === 'confirmed').length}
              </div>
            </div>
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white">
              <div className="text-sm font-medium opacity-90">En attente</div>
              <div className="mt-2 text-3xl font-bold">
                {registrations.filter(r => r.registration_status === 'pending').length}
              </div>
            </div>
            {!event?.is_free && (
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                <div className="text-sm font-medium opacity-90">Paiements vérifiés</div>
                <div className="mt-2 text-3xl font-bold">
                  {registrations.filter(r => r.payment_status === 'verified').length}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filtres */}
        <div className="mb-6 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Statut d'inscription</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvé</option>
                <option value="rejected">Rejeté</option>
                <option value="confirmed">Confirmé</option>
              </select>
            </div>

            {!event?.is_free && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Statut de paiement</label>
                <select
                  value={selectedPaymentStatus}
                  onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="verified">Vérifié</option>
                  <option value="failed">Échoué</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Rechercher</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nom, email, téléphone, code..."
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Liste des inscriptions */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Chargement des inscriptions...</p>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">Aucune inscription</h3>
              <p className="mt-2 text-sm text-gray-500">Aucune inscription ne correspond à vos critères de recherche.</p>
            </div>
          ) : (
            <>
              {filteredTotal > 0 && (
                <Pagination
                  placement="top"
                  currentPage={registrationsPage}
                  totalPages={registrationsTotalPages}
                  totalItems={filteredTotal}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setRegistrationsPage}
                  onPageSizeChange={(n) => setItemsPerPage(n)}
                />
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Participant
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Contact
                      </th>
                      {!event?.is_free && (
                        <>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Paiement
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                            Statut paiement
                          </th>
                        </>
                      )}
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Statut inscription
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Code confirmation
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Date inscription
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedRegistrations.map((reg) => (
                      <tr key={reg.id} className="hover:bg-indigo-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                              {(reg.member_name || `${reg.first_name || ''} ${reg.name || ''}`.trim() || 'N/A').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {reg.member_name || `${reg.first_name || ''} ${reg.name || ''}`.trim() || 'N/A'}
                              </div>
                              {reg.member_id ? (
                                <div className="text-xs text-indigo-600 font-medium">Membre</div>
                              ) : (
                                <div className="text-xs text-gray-500">Public</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-medium">
                            {reg.member_email || reg.email || '-'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {reg.member_phone || reg.phone || reg.whatsapp || '-'}
                          </div>
                        </td>
                        {!event?.is_free && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 font-medium">
                                {reg.payment_method || '-'}
                              </div>
                              {reg.payment_proof_url && (
                                <a
                                  href={reg.payment_proof_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1 mt-1"
                                >
                                  Voir la preuve
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                value={reg.payment_status || 'pending'}
                                onChange={(e) => handleUpdatePaymentStatus(reg.id, e.target.value)}
                                disabled={updatingRegistration === reg.id}
                                className={`text-xs font-semibold rounded-lg px-3 py-1.5 border-0 ${getPaymentStatusBadgeColor(reg.payment_status || 'pending')} ${updatingRegistration === reg.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80 transition'}`}
                              >
                                <option value="pending">En attente</option>
                                <option value="verified">Vérifié</option>
                                <option value="failed">Échoué</option>
                              </select>
                            </td>
                          </>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={reg.registration_status || 'pending'}
                            onChange={(e) => handleUpdateStatus(reg.id, e.target.value)}
                            disabled={updatingRegistration === reg.id}
                            className={`text-xs font-semibold rounded-lg px-3 py-1.5 border-0 ${getStatusBadgeColor(reg.registration_status || 'pending')} ${updatingRegistration === reg.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80 transition'}`}
                          >
                            <option value="pending">En attente</option>
                            <option value="approved">Approuvé</option>
                            <option value="rejected">Rejeté</option>
                            <option value="confirmed">Confirmé</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {reg.confirmation_code ? (
                            <div className="space-y-2">
                              <div className="text-sm font-mono font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg inline-block">
                                {reg.confirmation_code}
                              </div>
                              {reg.qr_code_url && (
                                <button
                                  onClick={() => {
                                    setSelectedRegistration(reg);
                                    setShowQRModal(true);
                                  }}
                                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                  </svg>
                                  Voir QR Code
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(reg.registered_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            {reg.confirmation_code && (
                              <button
                                onClick={() => handleSendConfirmation(reg.id, reg)}
                                disabled={sendingConfirmation === reg.id}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Envoyer la confirmation"
                              >
                                {sendingConfirmation === reg.id ? (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                                ) : (
                                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredTotal > 0 && (
                <Pagination
                  placement="bottom"
                  currentPage={registrationsPage}
                  totalPages={registrationsTotalPages}
                  totalItems={filteredTotal}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setRegistrationsPage}
                  onPageSizeChange={(n) => setItemsPerPage(n)}
                />
              )}
            </>
          )}
        </div>

        {/* Modal QR Code */}
        {showQRModal && selectedRegistration && (
          <ModalShell maxWidthClass="max-w-md" onBackdropClick={() => setShowQRModal(false)}>
            <div className="relative p-6 sm:p-8">
              <button
                onClick={() => setShowQRModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedRegistration.member_name || `${selectedRegistration.first_name || ''} ${selectedRegistration.name || ''}`.trim() || 'Participant'}
                </h3>
                <p className="text-gray-600 mb-6">Code de confirmation: <span className="font-mono font-bold text-indigo-600">{selectedRegistration.confirmation_code}</span></p>
                
                {selectedRegistration.qr_code_url && (
                  <div className="bg-white border-4 border-indigo-200 rounded-xl p-6 inline-block mb-6">
                    <img 
                      src={`${BACKEND_URL}${selectedRegistration.qr_code_url}`}
                      alt="QR Code de confirmation"
                      className="w-64 h-64 mx-auto"
                    />
                  </div>
                )}
                
                <p className="text-sm text-gray-500">
                  Scannez ce QR Code à l'entrée de l'événement pour confirmer la présence.
                </p>
              </div>
            </div>
          </ModalShell>
        )}
      </div>
    </div>
  );
};

export default EventRegistrations;

